import Link from "next/link";
import { getSkillCategoryEmoji } from "../skills/skill-category-emoji";
import type { ExploreSkillCard } from "../types";
import { SectionHeader } from "./section-header";

const SKILL_MONO_FONT =
  'ui-monospace, "SFMono-Regular", "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

function formatStars({ count }: { count: number | null }): string | null {
  if (count === null) {
    return null;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  }
  return `${count}`;
}

function StarIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-3 w-3"
      fill="currentColor"
      viewBox="0 0 16 16"
    >
      <path d="M8 .25a.75.75 0 01.673.418l1.882 3.815 4.21.612a.75.75 0 01.416 1.279l-3.046 2.97.719 4.192a.75.75 0 01-1.088.791L8 12.347l-3.766 1.98a.75.75 0 01-1.088-.79l.72-4.194L.818 6.374a.75.75 0 01.416-1.28l4.21-.611L7.327.668A.75.75 0 018 .25z" />
    </svg>
  );
}

function NewBadge() {
  return (
    <span className="inline-flex items-center rounded-sm bg-[var(--explore-badge-release-bg)] px-2 py-0.5 font-semibold text-[0.6rem] text-[var(--explore-badge-release-text)] uppercase tracking-[0.05em]">
      New
    </span>
  );
}

function SpotlightSkillCard({
  exploreBase,
  skill,
}: {
  exploreBase: string;
  skill: ExploreSkillCard;
}) {
  const emoji = getSkillCategoryEmoji({ category: skill.primaryCategory });
  const starLabel = formatStars({ count: skill.stars });
  const categoryLabel = skill.primaryCategory ?? "Skill";

  return (
    <Link
      className="group hover:-translate-y-px flex h-full flex-col gap-2.5 rounded-2xl border border-[color-mix(in_srgb,var(--explore-accent-yellow)_50%,var(--explore-border-light))] bg-[var(--explore-bg-card)] p-4 no-underline transition-all hover:border-[var(--explore-accent-yellow)] active:scale-[0.995]"
      href={`${exploreBase}/skills/${skill.slug}`}
      style={{
        fontFamily: "var(--homepage-sans-stack)",
        boxShadow: "var(--explore-card-shadow)",
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-[0.72rem] text-[var(--explore-text-muted)]">
          {emoji ? `${emoji} ` : ""}
          {categoryLabel}
        </span>
        <div className="flex shrink-0 items-center gap-1.5">
          {skill.isNew ? <NewBadge /> : null}
        </div>
      </div>

      <h3
        className="text-pretty font-semibold text-[0.95rem] text-[var(--explore-text-primary)] leading-[1.3]"
        style={{ fontFamily: SKILL_MONO_FONT }}
      >
        {skill.name}
      </h3>

      <p className="line-clamp-2 text-[0.82rem] text-[var(--explore-text-primary)] leading-[1.5]">
        {skill.description}
      </p>

      <div className="mt-auto flex items-center justify-between gap-2 text-[0.72rem] text-[var(--explore-text-muted)]">
        <span className="truncate">
          by {skill.author} · {skill.timeAgo}
        </span>
        {starLabel ? (
          <span className="inline-flex shrink-0 items-center gap-1 font-medium text-[var(--explore-text-secondary)]">
            <StarIcon />
            {starLabel}
          </span>
        ) : null}
      </div>
    </Link>
  );
}

export function SkillsSpotlightSection({
  className,
  exploreBase,
  skills,
}: {
  className?: string;
  exploreBase: string;
  skills: ExploreSkillCard[];
}) {
  if (skills.length === 0) {
    return null;
  }

  return (
    <section className={className}>
      <SectionHeader
        seeAllHref={`${exploreBase}/skills`}
        seeAllLabel="View all skills"
        subtitle="top by stars"
        title="Skills Spotlight"
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {skills.slice(0, 3).map((skill) => (
          <SpotlightSkillCard
            exploreBase={exploreBase}
            key={skill.id}
            skill={skill}
          />
        ))}
      </div>
    </section>
  );
}
